<template>
  <div
    v-if="getProductList?.length"
    class="pros-grid"
    :class="{ [zoneLevelCustomClass]: zoneLevelCustomClass }"
  >
    <component
      :is="customHeaderType ?? 'h3'"
      v-if="title"
      class="pros-grid-header"
    >
      {{ title }}
    </component>
    <div
      class="grid-x grid-margin-x grid-margin-y"
      :class="{ 'pros-grid-row-gap': extraRowGap }"
    >
      <slot />
      <li
        v-for="(product, index) in getProductList"
        :key="index"
        :class="gridCellStyle"
        @click="linkClickEvent(product.id, index + 1)"
      >
        <Skeleton
          v-if="loading && showSkeleton"
          height="300px"
        />
        <ProductThumbnail
          v-else
          :display-sale-price-only="!isMcom"
          :hide-review-text-and-rating="true"
          :image-width="getImageWidth"
          :open-in-new-tab="configData.openInNewTab"
          :product="product"
          :show-wish-list-btn-on-image="useWishList"
          :show-review-condensed="showReviewCondensed"
          :color-swatch-enhancement="colorSwatchEnhancement"
          :device-type="deviceType"
          :is-simplification-on="configData.isSimplificationOn"
          :override-price-simplification="configData.isSimplificationOn"
          :simplification-with-v1="configData.simplificationWithV1"
          :simplification-non-bold-percentage-exp="configData.simplificationNonBoldPercentageExp"
          :simplification-remove-sale-exp="configData.simplificationRemoveSaleExp"
          :suppress-tool-tip="true"
          :skeleton="true"
          :wrap-product-name="true"
          @wish-list-icon-clicked="(data) => wishListIconClicked(data, product.id, index + 1)"
        />
      </li>
    </div>
  </div>
</template>

<script>
import { ProductThumbnail } from '@common-vue/product-thumbnail';
import Skeleton from '@atomic-ui/skeleton';
import fetchProducts from '../../services/fetchProducts';
import {
  getGridCellStyle,
  getRequester,
  isValidProduct,
  isMcom,
  getProductThumbnailSize,
} from '../../utils/utils';
import constants from '../../utils/constants';
import {
  contentViewEventTag, fireHeartIconClickedTag,
  firePixelOrScrollEvent,
  linkClickEventTag,
} from '../../analytics/analyticsEvent';
import { productTransformerWithTDPFactory } from '../../utils/productTransformer';

const { SMALL, MEDIUM, LARGE } = constants.BREAK_POINT_TYPE;
const { BREAK_POINTS } = constants;

export default {
  name: 'ProsGrid',
  components: { ProductThumbnail, Skeleton },
  props: {
    showSkeleton: {
      type: Boolean,
      default: true,
    },
    configData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['linkClickEvent', 'dataLoadStatusEvent'],
  setup() {
    const entryPoint = process.env.ENTRY_POINT;
    const requester = getRequester(entryPoint);
    return {
      requester,
      isMcom: isMcom(),
    };
  },
  data() {
    return {
      testdata: null,
      observer: null,
      productsListSkeleton: [],
      productsList: [],
      loading: true,
      numberOfItems: this.configData.numberOfItems || {
        [SMALL]: { row: 5, column: 2 },
        [MEDIUM]: { row: 6, column: 4 },
        [LARGE]: { row: 5, column: 5 },
      },
      maxProduct: this.configData.maxProduct || 15,
      title: this.configData.title || '',
      useWishList: this.configData.useWishList,
      extraRowGap: this.configData.extraRowGap || 0,
      zoneLevelCustomClass: this.configData.zoneLevelCustomClass,
      customHeaderType: this.configData.customHeaderType,
      colorSwatchEnhancement: this.configData.colorSwatchEnhancement,
      showReviewCondensed: this.configData.showReviewCondensed,
      deviceType: this.configData.deviceType ? 'PHONE' : '',
    };
  },
  computed: {
    gridCellStyle() {
      return getGridCellStyle(this.numberOfItems);
    },
    getProductList() {
      return this.loading ? this.productsListSkeleton : this.productsList;
    },
    getProductIds() {
      return this.configData.productIds?.length > 0
        ? this.configData.productIds
        : this.configData.recommendationData?.recommendedIdsList?.map(
          (a) => a.id,
        );
    },
    getImageWidth() {
      return getProductThumbnailSize(this.numberOfItems);
    },
  },
  watch: {
    productsList(newValue, oldValue) {
      this.loading = false;
      const panelLoadedData = { workflow: this.configData.workflow };
      if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
        panelLoadedData.isLoaded = true;
        this.sendLoadedEvent(true);
      } else {
        panelLoadedData.isFailed = true;
        this.sendLoadedEvent(false);
      }
      contentViewEventTag(
        this.configData.recommendationData,
        this.configData.zone,
        this.configData.impressionData || {
          media_type: 'PROS:standard panels',
          content_position: 'R00S00',
        },
        this.configData.properties,
      );
      firePixelOrScrollEvent({
        name: 'PixelPresented',
        zone: this.configData.zone,
        requester: this.requester,
        encodedChoiceId: this.configData.recommendationData?.encodedChoiceId,
        contentId: this.configData.recommendationData?.contentId,
        pList: this.getProductIds,
        recProductIds:
          this.configData.recommendationData?.recommendedIdsList.map(
            (i) => i.id,
          ),
      });
      this.$store.commit(
        'recommendationStore/setPanelLoadedState',
        panelLoadedData,
      );
    },
  },
  beforeMount() {
    this.createSkeletonProductList();
  },
  mounted() {
    if (this.getProductIds) {
      this.getProductsAndValidate();
    }
  },
  methods: {
    async getProductsAndValidate() {
      const transformer = productTransformerWithTDPFactory({
        requester: this.requester,
        zone: this.configData?.zone,
        choiceId: this.configData?.recommendationData?.encodedChoiceId,
      },
      this.configData?.transformer,
      this.configData?.recommendationConfig?.selectedColorFamily,
      this.configData?.enableColorRecommendations,
      this.configData?.colorSwatchEnhancement);

      if (this.configData?.recommendationData?.productCollection?.length > 0) {
        this.productsList = this.configData?.recommendationData?.productCollection.map((product, index) => transformer(product, false, index));
      } else {
        this.productsList = await fetchProducts(
          this.getProductIds,
          this.productsListSkeleton.length,
          isValidProduct,
          false,
          {},
          this.configData,
        ).then((products) => products.map((product, index) => transformer(product, false, index)));
      }

      this.loading = false;
    },
    getDeviceType() {
      const { innerWidth } = window;
      if (innerWidth < BREAK_POINTS.SMALL) {
        return SMALL;
      } if (innerWidth < BREAK_POINTS.MEDIUM) {
        return MEDIUM;
      }
      return LARGE;
    },
    createSkeletonProductList() {
      let products = this.getProductIds;
      if (products) {
        const numberOfItems = this.numberOfItems[this.getDeviceType()];
        if (numberOfItems?.row && numberOfItems?.column) {
          products = products.slice(
            0,
            Math.ceil(numberOfItems.row * numberOfItems.column),
          );
        }
        if (this.maxProduct) {
          products = products.slice(0, this.maxProduct);
        }
      }
      this.productsListSkeleton = products;
    },
    linkClickEvent(productId, position) {
      linkClickEventTag(
        productId,
        position,
        this.configData.recommendationData,
        this.configData.zone,
      );
    },
    wishListIconClicked(data = {}, productId, position) {
      const { isInWishlist } = data;
      fireHeartIconClickedTag(
        isInWishlist,
        position,
        productId,
        this.configData.zone,
        this.title,
        this.configData.recommendationData,
      );
    },
    sendLoadedEvent(isLoaded) {
      this.$emit('dataLoadStatusEvent', isLoaded);
    },
  },
};
</script>

<style lang="scss" scoped>
.pros-grid {
  overflow: hidden;
  .cell {
    align-items: start;
    list-style-type: none;

    &.grid-column-5 {
      width: calc(20% - 1.5rem);
    }
  }
}

.pros-grid-header {
  padding-bottom: 32px;
}

.pros-grid-row-gap {
  row-gap: v-bind(extraRowGap);
}

@include breakpoint(small only) {
  .pros-add-to-bag-overlay {
    .pros-grid-header {
      padding-bottom: 16px;
    }

    &.pros-grid {
      --small-add-to-bag-grid-spacing: 0.25rem;

      .grid-x {
        margin: calc(-1 * var(--small-add-to-bag-grid-spacing));
      }

      .grid-thumbnail.small-6 {
        margin: var(--small-add-to-bag-grid-spacing);
        width: calc(50% - 2 * var(--small-add-to-bag-grid-spacing))
      }
    }
  }
}
</style>
